
import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMemoryOperations } from "@/hooks/useMemoryOperations";
import MemoryTable from "./memory/MemoryTable";
import MemoryTableHeader from "./memory/MemoryTableHeader";
import BulkDeleteDialog from "./memory/BulkDeleteDialog";
import RecategorizeMemoryDialog from "./memory/RecategorizeMemoryDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MessageSquare, FileText } from "lucide-react";
import { Memory } from "@/types/memory";

interface MemoryViewProps {
  selectedPersona: string | null;
  onBack?: () => void;
  showAllMemories?: boolean;
}

export const MemoryView: React.FC<MemoryViewProps> = ({ 
  selectedPersona, 
  onBack,
  showAllMemories = false 
}) => {
  const { currentUser } = useAuth();
  const {
    memories,
    isLoading,
    selectedMemories,
    setSelectedMemories,
    fetchMemories,
    deleteMemory,
    bulkDeleteMemories,
    recategorizeMemory,
    updateSecurityLevel,
    isDeleting,
    isRecategorizing,
  } = useMemoryOperations();

  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showRecategorizeDialog, setShowRecategorizeDialog] = useState(false);
  const [memoryToRecategorize, setMemoryToRecategorize] = useState<Memory | null>(null);

  useEffect(() => {
    if (currentUser?.id) {
      fetchMemories(currentUser.id, selectedPersona);
    }
  }, [currentUser?.id, selectedPersona, fetchMemories]);

  const handleBulkDelete = async () => {
    if (selectedMemories.length === 0) return;
    
    const success = await bulkDeleteMemories(selectedMemories);
    if (success && currentUser?.id) {
      setSelectedMemories([]);
      fetchMemories(currentUser.id, selectedPersona);
      setShowBulkDeleteDialog(false);
    }
  };

  const handleRecategorize = async (memoryId: number, newCategory: string) => {
    const success = await recategorizeMemory(memoryId, newCategory);
    if (success && currentUser?.id) {
      fetchMemories(currentUser.id, selectedPersona);
      setShowRecategorizeDialog(false);
      setMemoryToRecategorize(null);
    }
  };

  const handleDelete = async (memoryId: number) => {
    await deleteMemory(memoryId);
  };

  const handleRecategorizeClick = (memory: Memory) => {
    setMemoryToRecategorize(memory);
    setShowRecategorizeDialog(true);
  };

  const handleUpdateSecurityLevel = async (memoryId: number, securityLevel: 'low' | 'medium' | 'high') => {
    await updateSecurityLevel(memoryId, securityLevel);
  };

  const filteredMemories = memories.filter(memory =>
    memory.memory.toLowerCase().includes(searchTerm.toLowerCase()) ||
    memory.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (memory.filename && memory.filename.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Separate memories and files
  const regularMemories = filteredMemories.filter(memory => !memory.filename);
  const fileMemories = filteredMemories.filter(memory => memory.filename);

  const pageTitle = showAllMemories 
    ? 'All Memories' 
    : selectedPersona 
      ? `${selectedPersona.charAt(0).toUpperCase() + selectedPersona.slice(1)} Memories` 
      : 'All Memories';

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please log in to view your memories.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>
      </div>

      <MemoryTableHeader
        pageTitle={pageTitle}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCount={selectedMemories.length}
        onBulkDelete={() => setShowBulkDeleteDialog(true)}
      />

      <Tabs defaultValue="memories" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-muted/30 backdrop-blur-sm border border-border">
          <TabsTrigger 
            value="memories" 
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <MessageSquare className="h-4 w-4" />
            Memories ({regularMemories.length})
          </TabsTrigger>
          <TabsTrigger 
            value="files" 
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <FileText className="h-4 w-4" />
            Files ({fileMemories.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="memories" className="mt-6">
          <MemoryTable
            memories={regularMemories}
            isLoading={isLoading}
            selectedMemories={selectedMemories}
            onSelectionChange={setSelectedMemories}
            onDeleteMemory={handleDelete}
            onRecategorizeMemory={handleRecategorizeClick}
            onUpdateSecurityLevel={handleUpdateSecurityLevel}
            showFileName={false}
          />
        </TabsContent>
        
        <TabsContent value="files" className="mt-6">
          <MemoryTable
            memories={fileMemories}
            isLoading={isLoading}
            selectedMemories={selectedMemories}
            onSelectionChange={setSelectedMemories}
            onDeleteMemory={handleDelete}
            onRecategorizeMemory={handleRecategorizeClick}
            onUpdateSecurityLevel={handleUpdateSecurityLevel}
            showFileName={true}
          />
        </TabsContent>
      </Tabs>

      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
        onConfirm={handleBulkDelete}
        count={selectedMemories.length}
      />

      <RecategorizeMemoryDialog
        open={showRecategorizeDialog}
        onOpenChange={setShowRecategorizeDialog}
        onConfirm={handleRecategorize}
        memory={memoryToRecategorize}
      />
    </div>
  );
};
